import {
    ClientCampaignBooking,
    ClientCampaignTiktokPostStats,
    ClientCampaignTiktokPostStatsTotals,
    TiktokUserImage,
} from '@round/api';
import { useMemo } from 'react';
import Table from '../../../../../ui/Table/Table';
import { Column, Row } from 'react-table';
import { Image } from '@round/ui-kit';
import styles from './PostStatsTable.module.css';
import { displayOptionalNumericTableValue, numberWithCommas } from '../../../../../utils/valueFormatHelpers';
import { buildTiktokUserUrl } from '../../../../../utils/utits';
import { renderPostStatsTableBody } from './PostStatsTable.helpers';
import uniqBy from 'lodash/uniqBy';
import StatusPicker from '../../../../booking/TableCells/StatusPicker/StatusPicker';

type TiktokPostStatsTableProps = {
    loading: boolean;
    error: boolean;
    data: TiktokPostStatsTableRow[];
    totals: ClientCampaignTiktokPostStatsTotals | null;
};

export type TiktokPostStatsTableRow = Partial<ClientCampaignTiktokPostStats> & {
    booking: ClientCampaignBooking;
    userImage: TiktokUserImage | null;
};

const TiktokPostStatsTable = ({ loading, error, data, totals }: TiktokPostStatsTableProps) => {
    const columns: Column<TiktokPostStatsTableRow>[] = useMemo(
        () => [
            {
                Header: 'Account',
                id: 'account',
                Cell: ({ row }: { row: Row<TiktokPostStatsTableRow> }) => {
                    const username = row.original.booking.influencer.tiktok_username;
                    const userProfileUrl = username ? buildTiktokUserUrl(username) : '';

                    const userImage = row.original.userImage;
                    const imageUrl = userImage?.avatar_thumb.cached_url || userImage?.avatar_thumb.original_url || '';

                    return (
                        <div className={styles.accountCell}>
                            <Image src={imageUrl} className={styles.userImage} alt="user" />
                            {username ? (
                                <a href={userProfileUrl} target="_blank" rel="noreferrer">
                                    {username}
                                </a>
                            ) : (
                                <span>-</span>
                            )}
                        </div>
                    );
                },
            },
            {
                Header: 'Status',
                id: 'status',
                Cell: ({ row }: { row: Row<TiktokPostStatsTableRow> }) => {
                    const status = row.original.booking.status;

                    return <StatusPicker readonly status={status} />;
                },
            },
            {
                Header: 'Followers',
                id: 'followers',
                Cell: ({ row }: { row: Row<TiktokPostStatsTableRow> }) => {
                    const followerCount = row.original.booking.influencer.tiktok_follower_count;

                    return <>{displayOptionalNumericTableValue(followerCount)}</>;
                },
            },
            {
                Header: 'Video Creation Time',
                accessor: 'video_create_time',
                Cell: ({ value: videoCreateTime }) => {
                    if (!videoCreateTime) {
                        return <>-</>;
                    }

                    const date = new Date(videoCreateTime);
                    const dateLiteral = date.toLocaleString('en-US');
                    return <>{dateLiteral}</>;
                },
            },
            {
                Header: 'Video Plays',
                accessor: 'play_count',
                Cell: ({ value: playCount }) => <>{displayOptionalNumericTableValue(playCount)}</>,
            },
            {
                Header: 'Likes',
                accessor: 'digg_count',
                Cell: ({ value: diggCount }) => <>{displayOptionalNumericTableValue(diggCount)}</>,
            },
            {
                Header: 'Comments',
                accessor: 'comment_count',
                Cell: ({ value: commentCount }) => <>{displayOptionalNumericTableValue(commentCount)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                Cell: ({ value: shareCount }) => <>{displayOptionalNumericTableValue(shareCount)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value: saveCount }) => <>{displayOptionalNumericTableValue(saveCount)}</>,
            },
            {
                Header: 'Ad Code',
                accessor: 'ad_code',
                Cell: ({ value: adCode }) => <>{adCode || '-'}</>,
            },
        ],
        []
    );

    const totalFollowerCount = useMemo(
        () =>
            uniqBy(data, (row) => row.booking.influencer.id)
                .map((row) => row.booking.influencer.tiktok_follower_count || 0)
                .reduce((agg, followers) => agg + followers, 0),
        [data]
    );

    return (
        <Table
            columns={columns}
            data={data}
            className={styles.table}
            renderBody={renderPostStatsTableBody(loading, error)}
            renderFooter={() => (
                <tfoot>
                    <tr>
                        <td>TOTAL</td>
                        <td />
                        <td>{totalFollowerCount ? numberWithCommas(totalFollowerCount) : '-'}</td>
                        <td />
                        <td>{totals?.play_count ? numberWithCommas(totals.play_count) : '-'}</td>
                        <td>{totals?.digg_count ? numberWithCommas(totals.digg_count) : '-'}</td>
                        <td>{totals?.comment_count ? numberWithCommas(totals.comment_count) : '-'}</td>
                        <td>{totals?.share_count ? numberWithCommas(totals.share_count) : '-'}</td>
                        <td>{typeof totals?.save_count === 'number' ? numberWithCommas(totals.save_count) : '-'}</td>
                        <td />
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default TiktokPostStatsTable;
